import type { QuizEvent, SubmissionPayload } from "@/QuizWidget";
import { readUtms } from "@/utils/utms";

// Frontend in-memory event names → backend EventType wire values.
const EVENT_TYPE_MAP: Record<QuizEvent["type"], string> = {
  start: "started",
  view: "question_viewed",
  answer: "question_answered",
  complete: "completed",
  result_view: "result_viewed",
  cta_click: "cta_clicked",
};

export function createTransport(
  base: string,
  opts?: { preview?: boolean },
): {
  onSubmit: (payload: SubmissionPayload) => void;
  onEvent: (event: QuizEvent) => void;
} {
  const origin = base.replace(/\/$/, "");
  // Preview runs render the live widget but must never write analytics.
  const preview = opts?.preview ?? false;
  // UTM snapshot: read once per transport (≈ once per page load). The params
  // describe the landing URL, which does not change mid-quiz.
  const utms = readUtms();

  const post = (path: string, body: Record<string, unknown>): void => {
    if (preview) return;
    if (typeof fetch !== "function") return;
    fetch(`${origin}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // keepalive lets a small lead/event POST survive a page transition (~64KB
      // body cap — fine for analytics; don't reuse this transport for uploads).
      keepalive: true,
      body: JSON.stringify(body),
    }).catch(() => {
      // Best-effort: swallow network failures so they never break the quiz UX.
    });
  };

  return {
    onSubmit: (p) =>
      post("/api/submissions", {
        quiz_slug: p.quizSlug,
        session_id: p.sessionId,
        persona: p.persona,
        answers: p.answers,
        ...(utms ? { utms } : {}),
      }),
    onEvent: (e) =>
      post("/api/events", {
        quiz_slug: e.quizSlug,
        session_id: e.sessionId,
        event_type: EVENT_TYPE_MAP[e.type],
        question_id: e.questionId,
        answer_id: e.answerId,
        ...(utms ? { utms } : {}),
        // Client wall-clock at emit: the API uses it only for duration math
        // (keepalive POSTs can arrive late), never for windowing.
        client_created_at: new Date().toISOString(),
      }),
  };
}
